import React, { useEffect, useState } from 'react';
import { Form, Input, Col, Row, Modal, Select, message, InputNumber, Divider } from 'antd';
import { UploadOutlined, FileTextOutlined } from '@ant-design/icons';
import Button from '../../../../shared/globalStyling/Button';
import { defaultRadius, stateList } from '../../../../../core/utils/constants/constants';
import { metersToMiles, kmToMiles, milesToKm } from '../../../../../core/utils/campaigns';
import { getFormattedAddress } from '../../../../shared/maps/utils/mapUtils';
import { OrganizationConfig } from '../../configs/organizationConfig';
import { MapConfig } from '../../../../shared/maps/configs/mapConfigs';

export default function TargetingLocationForm({
  selectedLocation,
  isFormOpen,
  closeForm,
  submitForm,
  google,
  targetingLocations,
  onCsvUploadComplete,
  csvUploadComponent,
  csvTemplateComponent,
}) {
  const { initialLocation } = OrganizationConfig;
  const { countryType } = MapConfig;

  const [form] = Form.useForm();
  const [location, setLocation] = useState(initialLocation);
  const [country, setCountry] = useState('USA');
  const [street, setStreet] = useState();
  const [circles, setCircles] = useState([]);
  const [formTitle, setFormTitle] = useState('Create Targeting Location');
  const [isOutsideUSAndCanada, setIsOutsideUSAndCanada] = useState(false);
  const [radiusUnit, setRadiusUnit] = useState('miles');
  const [previousUnit, setPreviousUnit] = useState('miles');
  const [hasChanges, setHasChanges] = useState(false);
  const [originalValues, setOriginalValues] = useState({});

  useEffect(() => {
    searchInputPlacesInit();
  });

  useEffect(() => {
    if (selectedLocation) {
      setStreet(selectedLocation.street || '');

      if (
        selectedLocation.geoframe &&
        selectedLocation.geoframe.circles &&
        selectedLocation.geoframe.circles[0] &&
        selectedLocation.geoframe.circles[0].coordinate
      ) {
        const coordinate = selectedLocation.geoframe.circles[0].coordinate;
        const location = new google.maps.LatLng(coordinate.lat, coordinate.lng);
        updateCirclesOverlay({
          location,
        });
      }

      setIsOutsideUSAndCanada(
        selectedLocation.country !== 'USA' && selectedLocation.country !== 'CANADA'
      );

      // When editing, set the radius unit to miles since we display in miles
      if (
        selectedLocation.geoframe &&
        selectedLocation.geoframe.circles &&
        selectedLocation.geoframe.circles[0]
      ) {
        setRadiusUnit('miles');
        setPreviousUnit('miles');
      }

      // Store original values for change detection
      const initialValues = {
        name: selectedLocation.name || '',
        formattedAddress: selectedLocation.formattedAddress || '',
        city: selectedLocation.city || '',
        state: selectedLocation.state || '',
        province: selectedLocation.province || '',
        zip: selectedLocation.zip || '',
        radius: getInitialRadius(),
      };
      setOriginalValues(initialValues);
      setHasChanges(false);

      setFormTitle('Edit Targeting Location');
    } else {
      setOriginalValues({});
      setHasChanges(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation]);

  const onCloseForm = () => {
    form.resetFields();
    setRadiusUnit('miles');
    setPreviousUnit('miles');
    setHasChanges(false);
    setTimeout(() => {
      closeForm();
    }, 500);
  };

  const checkForChanges = () => {
    if (!selectedLocation) {
      setHasChanges(true);
      return;
    }

    const currentValues = form.getFieldsValue();

    const formFieldsChanged = Object.keys(originalValues).some(key => {
      const currentValue = currentValues[key] || '';
      const originalValue = originalValues[key] || '';
      return String(currentValue) !== String(originalValue);
    });

    const streetChanged = (street || '') !== (selectedLocation.street || '');

    setHasChanges(formFieldsChanged || streetChanged);
  };

  const searchInputPlacesInit = () => {
    const input = document.getElementById('search-input-tl');

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
      }
    });

    var options = {
      componentRestrictions: MapConfig.componentRestrictions, // This includes the country restriction
    };

    const searchBox = new google.maps.places.Autocomplete(input, options);

    searchBox.addListener('place_changed', () => {
      const place = searchBox.getPlace();

      if (!place) {
        message.error('Error: No places found!');
        return;
      }

      const location = place.geometry.location;

      updateFormField(place);
      updateCirclesOverlay({ location });

      setLocation({
        lat: location.lat(),
        lng: location.lng(),
      });

      setTimeout(checkForChanges, 100);
    });
  };

  const updateFormField = (place) => {
    const { state, city, zip, country, address } = getFormattedAddress({
      result: place,
    });

    const notInUSAorCanada = country !== 'US' && country !== 'CA';

    setIsOutsideUSAndCanada(notInUSAorCanada);

    form.setFieldsValue({
      state: notInUSAorCanada ? '' : state.replace(/_/g, ' ').toUpperCase(),
      city,
      zip,
      formattedAddress: place.formatted_address,
      province: notInUSAorCanada ? state : '',
    });

    setCountry(countryType[country]);
    setStreet(address);
  };

  const updateCirclesOverlay = ({ location }) => {
    const newCircle = {
      overlay: new google.maps.Circle({
        center: { lat: location.lat(), lng: location.lng() },
        radius: defaultRadius,
      }),
      type: 'circle',
    };
    setCircles([newCircle]);
  };

  const onSubmit = () => {
    form.validateFields().then(() => {
      if (
        targetingLocations &&
        targetingLocations.find(
          (location) =>
            location.name.toLowerCase() === form.getFieldsValue().name.toLowerCase() &&
            (!selectedLocation || location.id !== selectedLocation.id)
        )
      ) {
        message.error('Target location name already exists!');
        return;
      }

      if (!circles || circles.length === 0 || !circles[0].overlay) {
        message.error('Invalid location. Please select a valid address.');
        return;
      }

      let radius = form.getFieldValue('radius');
      radius = convertRadius(radius, radiusUnit);

      function convertRadius(radius, radiusUnit) {
        let radiusInMiles;
        switch (radiusUnit) {
          case 'kilometers':
            radiusInMiles = kmToMiles(radius);
            break;
          case 'meters':
            radiusInMiles = metersToMiles(radius);
            break;
          default:
            radiusInMiles = radius; // miles
        }
        // Convert miles to millimiles for storage (divide by 1000)
        return radiusInMiles;
      }

      const data = {
        ...form.getFieldsValue(),
        ...location,
        street,
        country,
        circles: [
          {
            ...circles[0],
            overlay: {
              ...circles[0].overlay,
              radius,
            },
          },
        ],
        radiusUnit,
        audienceType: 'TARGETING',
      };

      // Replace spaces with underscores and convert to uppercase for the 'state' property
      if (!data.state) {
        return message.error('Invalid State or State is not supported yet.');
      }

      data.state = data.state.replace(/ /g, '_').toUpperCase();

      if (isOutsideUSAndCanada) {
        delete data.state;
      } else {
        delete data.province;
      }

      submitForm(data);
    });
  };

  const getInitialRadius = () => {
    if (
      selectedLocation &&
      selectedLocation.geoframe &&
      selectedLocation.geoframe.circles &&
      selectedLocation.geoframe.circles[0] &&
      selectedLocation.geoframe.circles[0].radius
    ) {
      // The stored radius is in millimiles, multiply by 1000 to convert to miles for display
      return selectedLocation.geoframe.circles[0].radius * 1000;
    }
    return defaultRadius;
  };

  const radiusMinByUnit = {
    kilometers: 0.1,
    meters: 100,
    miles: 0.0621,
  };

  const convertRadiusValue = (value, fromUnit, toUnit) => {
    if (fromUnit === toUnit || value === 0) return value;

    // Convert to miles first (our base unit)
    let radiusInMiles = value;
    if (fromUnit === 'kilometers') {
      radiusInMiles = kmToMiles(value);
    } else if (fromUnit === 'meters') {
      radiusInMiles = metersToMiles(value);
    }

    // Convert from miles to target unit
    if (toUnit === 'kilometers') {
      return milesToKm(radiusInMiles);
    } else if (toUnit === 'meters') {
      return radiusInMiles / 0.000621371; // inverse of metersToMiles conversion
    }

    return radiusInMiles; // already in miles
  };

  const handleRadiusUnitChange = (newUnit) => {
    const currentRadius = form.getFieldValue('radius');
    if (currentRadius && radiusUnit !== newUnit) {
      const convertedRadius = convertRadiusValue(currentRadius, radiusUnit, newUnit);
      // Round to appropriate precision based on unit
      let roundedRadius;
      if (newUnit === 'meters') {
        roundedRadius = Math.round(convertedRadius * 100) / 100; // 2 decimal places
      } else if (newUnit === 'kilometers') {
        roundedRadius = Math.round(convertedRadius * 10000) / 10000; // 4 decimal places
      } else {
        roundedRadius = Math.round(convertedRadius * 10000) / 10000; // 4 decimal places for miles
      }
      form.setFieldsValue({ radius: roundedRadius });
      // Trigger validation after setting the new value
      setTimeout(() => {
        form.validateFields(['radius']);
      }, 100);
    }
    setPreviousUnit(radiusUnit);
    setRadiusUnit(newUnit);

    setTimeout(checkForChanges, 150);
  };
  return (
    <>
      <Modal
        visible={isFormOpen}
        title={formTitle}
        onOk={() => onSubmit()}
        onCancel={onCloseForm}
        footer={null}
        wrapClassName="targeting-root"
      >
        {/* CSV import section — only on create, not edit */}
        {!selectedLocation && csvUploadComponent && (
          <div className="targeting-form__import">
            <div className="targeting-form__import-template">
              {csvTemplateComponent}
            </div>
            <div className="targeting-form__import-row">
              <span className="targeting-form__import-label">Upload in Bulk (CSV)</span>
              {csvUploadComponent}
            </div>
            <Divider plain className="targeting-form__divider">
              or add manually
            </Divider>
          </div>
        )}

        <Row gutter={18} justify="center">
          <Form
            name="form-name"
            form={form}
            layout="vertical"
            onFinish={() => onSubmit()}
            onValuesChange={checkForChanges}
            autoComplete="off"
            validateTrigger="onBlur"
            initialValues={{
              name: selectedLocation ? selectedLocation.name : '',
              formattedAddress: selectedLocation ? selectedLocation.formattedAddress : '',
              city: selectedLocation ? selectedLocation.city : '',
              state: selectedLocation ? selectedLocation.state : '',
              province:
                selectedLocation && selectedLocation.province ? selectedLocation.province : '',
              zip: selectedLocation ? selectedLocation.zip : '',
              radius: getInitialRadius(),
            }}
          >
            <Form.Item
              label="Name"
              name="name"
              rules={[
                {
                  required: true,
                  message: 'Target name is required!',
                },
              ]}
            >
              <Input name="name" autoComplete="no" />
            </Form.Item>
            <Form.Item
              label="Address (Type and select the address) "
              name="formattedAddress"
              rules={[
                {
                  required: true,
                  message: 'Address is required!',
                },
              ]}
            >
              <input
                type="input"
                placeholder="Address"
                name="formattedAddress"
                id="search-input-tl"
                className="ant-input"
                onFocus={(e) => {
                  e.target.setAttribute('autocomplete', 'no');
                }}
              />
            </Form.Item>
            <Form.Item
              label="Radius"
              name="radius"
              tooltip="The radius will be automatically converted to the dashboard's default unit, miles."
              rules={[
                {
                  required: true,
                  message: 'Invalid radius value!',
                },
              ]}
            >
              <InputNumber
                min={radiusMinByUnit[radiusUnit]}
                name="radius"
                style={{ width: '100%' }}
                max={999999999999999} // max number to prevent overflow
                addonAfter={
                  <Select
                    value={radiusUnit}
                    onChange={handleRadiusUnitChange}
                    style={{ width: 120 }}
                  >
                    <Select.Option value="miles">Miles</Select.Option>
                    <Select.Option value="meters">Meters</Select.Option>
                    <Select.Option value="kilometers">Kilometers</Select.Option>
                  </Select>
                }
              />
            </Form.Item>
            <Row>
              <Col xs={24} xl={9} style={{ paddingRight: 5 }}>
                <Form.Item
                  label="City"
                  name="city"
                  rules={[
                    {
                      required: true,
                      message: 'City is required!',
                    },
                  ]}
                  wrapperCol={{ span: 24 }}
                >
                  <Input placeholder="City" name="city" autoComplete="no" />
                </Form.Item>
              </Col>
              <Col xs={24} xl={9}>
                {isOutsideUSAndCanada ? (
                  <Form.Item
                    label="State"
                    name="province"
                    rules={[
                      {
                        required: true,
                        message: 'State is required!',
                      },
                    ]}
                    wrapperCol={{ span: 24 }}
                  >
                    {/* This is the state for outside US and Canada */}
                    <Input placeholder="State" name="province" />
                  </Form.Item>
                ) : (
                  <Form.Item
                    label="State"
                    name="state"
                    rules={[
                      {
                        required: true,
                        message: 'State is required!',
                      },
                    ]}
                    wrapperCol={{ span: 24 }}
                  >
                    <Select
                      showSearch
                      placeholder="Select State"
                      name="state"
                      style={{ width: '100%', borderRadius: '0px' }}
                    >
                      {stateList.map((item) => (
                        <Select.Option key={item} value={item}>
                          {item.replace('_', ' ')}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}
              </Col>
              <Col xs={24} xl={6} style={{ paddingLeft: 5 }}>
                <Form.Item
                  label="Zip"
                  name="zip"
                  rules={[{ required: true, message: 'Zip is required!' }]}
                  wrapperCol={{ span: 24 }}
                >
                  <Input placeholder="Zip" name="zip" autoComplete="no" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item>
              <Row justify="end">
                <Button type="default" onClick={() => onCloseForm()}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" disabled={selectedLocation && !hasChanges}>
                  Submit
                </Button>
              </Row>
            </Form.Item>
          </Form>
        </Row>
      </Modal>
    </>
  );
}
